import { useMemo, useState } from "react";
import { MessageCircleHeart, ChevronDown, CornerDownRight } from "lucide-react";

const snippet = (t, n = 60) => {
  const s = (t || "").trim();
  return s.length > n ? `${s.slice(0, n)}…` : s;
};

export const AllCommentsPanel = ({ trip, defaultOpen = false, onJump }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [seenAt] = useState(() => localStorage.getItem(`vr_comments_seen_${trip.id}`) || "");
  const logs = trip.log_entries || [];
  const comments = trip.comments || [];
  const reactions = trip.reactions || [];
  const commentReactions = trip.comment_reactions || [];

  const items = useMemo(() => {
    const entryById = Object.fromEntries(logs.map((e) => [e.id, e]));
    const commentById = Object.fromEntries(comments.map((c) => [c.id, c]));
    const entryCtx = (eid) => {
      const e = entryById[eid];
      if (!e) return { day: null, ctx: "bij een verwijderd moment" };
      return { day: e.day_number ?? null, ctx: `bij "${snippet(e.text, 42) || "moment zonder tekst"}"` };
    };
    const list = [];
    comments.forEach((c) => {
      let day = c.day_number ?? null;
      let ctx = day != null ? `Dag ${day}` : "Algemeen";
      if (c.entry_id) {
        const ec = entryCtx(c.entry_id);
        day = ec.day ?? day;
        ctx = `${day != null ? `Dag ${day} · ` : ""}${ec.ctx}`;
      } else if (day != null) {
        ctx = `Dag ${day} · dag-reactie`;
      }
      list.push({ key: `c-${c.id}`, ts: c.created_at, author: c.author_name, isOwner: c.is_owner, isReply: !!c.parent_id, text: c.text, ctx, day });
    });
    reactions.forEach((r) => {
      const ec = entryCtx(r.entry_id);
      list.push({ key: `r-${r.id}`, ts: r.created_at, author: r.author_name, isOwner: r.is_owner, emoji: r.emoji, ctx: `${ec.day != null ? `Dag ${ec.day} · ` : ""}${ec.ctx}`, day: ec.day });
    });
    commentReactions.forEach((r) => {
      const c = commentById[r.comment_id];
      const day = c?.day_number ?? null;
      list.push({ key: `cr-${r.id}`, ts: r.created_at, author: r.author_name, isOwner: r.is_owner, emoji: r.emoji, ctx: `${day != null ? `Dag ${day} · ` : ""}op reactie van ${c ? c.author_name : "onbekend"}`, day });
    });
    return list.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
  }, [logs, comments, reactions, commentReactions]);

  if (items.length === 0) return null;
  const newCount = items.filter((it) => !it.isOwner && it.ts > seenAt).length;

  return (
    <div className="bg-[#B5838D]/5 rounded-2xl" data-testid="all-comments-panel">
      <button data-testid="all-comments-toggle" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 p-5 text-left">
        <p className="font-heading font-bold text-sm flex items-center gap-2">
          <MessageCircleHeart className="w-4 h-4 text-[#B5838D]" /> Alle reacties ({items.length})
          {newCount > 0 && (
            <span data-testid="all-comments-new-badge" className="min-w-[16px] h-4 px-1.5 rounded-full bg-[#B5838D] text-white text-[10px] font-bold inline-flex items-center justify-center">{newCount} nieuw</span>
          )}
        </p>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-1.5 max-h-96 overflow-y-auto" data-testid="all-comments-list">
          {items.map((it) => (
            <button key={it.key} data-testid={`all-comments-item-${it.key}`}
              onClick={() => onJump?.(it.day)}
              className="w-full text-left bg-card rounded-xl px-3.5 py-2.5 hover:ring-1 hover:ring-[#B5838D]/40 transition-[box-shadow] duration-200">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm leading-snug min-w-0">
                  <span className="font-semibold">{it.author}</span>
                  {it.isOwner && <span className="ml-1 text-[9px] font-bold tracking-wider uppercase bg-primary/10 text-primary rounded-full px-1.5 py-0.5">reiziger</span>}
                  {it.emoji ? (
                    <span className="text-muted-foreground"> reageerde met <span className="text-base align-middle">{it.emoji}</span></span>
                  ) : (
                    <span className="text-muted-foreground">{it.isReply ? " antwoordde" : ""}: {snippet(it.text, 90)}</span>
                  )}
                </p>
                {!it.isOwner && it.ts > seenAt && <span className="shrink-0 w-2 h-2 rounded-full bg-[#B5838D] mt-1.5" title="Nieuw" />}
              </div>
              <p className="text-[11px] text-muted-foreground font-mono mt-1 flex items-center gap-1">
                <CornerDownRight className="w-3 h-3" /> {it.ctx} · {it.ts ? new Date(it.ts).toLocaleString("nl-BE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
              </p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
